import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Brand } from "@/components/Brand";
import { toast } from "sonner";
import { CheckCircle, XCircle, Spinner, Copy, Eye, EyeSlash, ArrowRight } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DemoInvite() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const [state, setState] = useState({ status: "loading" });
    const [showPw, setShowPw] = useState(false);
    // StrictMode fires effects twice in dev — since /auth/magic-link is one-shot,
    // the 2nd call always returns 400 and overwrites the success state. Sentinel
    // guarantees the exchange runs exactly once per mount.
    const exchanged = useRef(false);

    useEffect(() => {
        if (exchanged.current) return;
        exchanged.current = true;
        const uid = params.get("uid");
        const token = params.get("token");
        if (!uid || !token) {
            setState({ status: "error", message: "This invite link is malformed. Ask your host to resend it." });
            return;
        }
        (async () => {
            try {
                const r = await axios.post(`${API}/auth/magic-link`, { uid, token });
                localStorage.setItem("utal_token", r.data.token);
                setState({
                    status: "ok",
                    user: r.data.user,
                    password: r.data.generated_password,
                });
            } catch (e) {
                const detail = e?.response?.data?.detail || "Invite could not be verified.";
                setState({ status: "error", message: detail });
            }
        })();
    }, [params]);

    const copy = (v, label) => {
        navigator.clipboard.writeText(v);
        toast.success(`${label} copied`);
    };

    const continueToApp = () => {
        // AuthContext will re-fetch /auth/me on next mount and pick up the JWT.
        window.location.assign("/app");
    };

    return (
        <div className="min-h-screen bg-[#101012] text-white flex items-center justify-center p-6" data-testid="demo-invite-page">
            <div className="max-w-lg w-full space-y-6">
                <Brand size="lg" variant="dark" dataTestId="invite-brand" />

                <Card className="p-8 bg-[#151517] border-white/10">
                    {state.status === "loading" && (
                        <div className="text-center space-y-4" data-testid="invite-loading">
                            <Spinner size={40} weight="thin" className="animate-spin text-white/70 mx-auto" />
                            <div className="text-[10px] tracking-[0.35em] uppercase text-primary font-bold">Verifying your invite…</div>
                            <p className="text-sm text-white/70">Setting up your UTAL MEA credentials.</p>
                        </div>
                    )}

                    {state.status === "ok" && (
                        <div className="space-y-6" data-testid="invite-ok">
                            <div className="text-center space-y-3">
                                <CheckCircle size={44} weight="fill" className="text-emerald-500 mx-auto" />
                                <div className="text-[10px] tracking-[0.35em] uppercase text-emerald-500 font-bold">Access granted</div>
                                <p className="text-sm text-white/80">Welcome, <strong>{state.user?.full_name}</strong>. Your credentials are ready.</p>
                            </div>

                            <div className="space-y-3 border border-primary/30 bg-primary/5 rounded p-4">
                                <div className="text-[10px] tracking-[0.35em] uppercase text-primary font-bold">· Your sign-in credentials</div>

                                <div>
                                    <div className="text-[9px] tracking-[0.25em] uppercase text-white/50 font-bold mb-1">Email</div>
                                    <div className="flex items-center justify-between gap-2 bg-black/40 border border-white/10 rounded px-3 py-2">
                                        <span className="mono text-sm truncate" data-testid="issued-email">{state.user?.email}</span>
                                        <button onClick={() => copy(state.user?.email, "Email")} className="text-white/60 hover:text-white shrink-0" data-testid="copy-email">
                                            <Copy size={14} weight="bold" />
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <div className="text-[9px] tracking-[0.25em] uppercase text-white/50 font-bold mb-1">Password <span className="text-primary">(shown once)</span></div>
                                    <div className="flex items-center justify-between gap-2 bg-black/40 border border-white/10 rounded px-3 py-2">
                                        <span className="mono text-sm truncate" data-testid="issued-password">
                                            {showPw ? state.password : "•".repeat(state.password?.length || 14)}
                                        </span>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <button onClick={() => setShowPw((v) => !v)} className="text-white/60 hover:text-white" data-testid="toggle-pw">
                                                {showPw ? <EyeSlash size={14} weight="bold" /> : <Eye size={14} weight="bold" />}
                                            </button>
                                            <button onClick={() => copy(state.password, "Password")} className="text-white/60 hover:text-white" data-testid="copy-pw">
                                                <Copy size={14} weight="bold" />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <p className="text-[11px] text-white/60 leading-relaxed">
                                    Save these credentials — the password is generated once and won't be shown again.
                                    On any future visit, sign in at <span className="mono text-white/80">/login</span> with the email and password above.
                                </p>
                                <p className="text-[11px] text-primary/90 leading-relaxed border-l-2 border-primary/50 pl-3">
                                    <strong>Important:</strong> Use <strong>email + password</strong> to sign in — do not use "Continue with Google". Google Sign-In is disabled for invited demo users.
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                                <Button variant="outline" onClick={() => nav("/login")} data-testid="invite-go-login" className="gap-2">
                                    Sign-in page
                                </Button>
                                <Button onClick={continueToApp} data-testid="invite-continue" className="gap-2">
                                    Continue to app <ArrowRight size={14} weight="bold" />
                                </Button>
                            </div>
                        </div>
                    )}

                    {state.status === "error" && (
                        <div className="text-center space-y-4" data-testid="invite-error">
                            <XCircle size={48} weight="fill" className="text-primary mx-auto" />
                            <div className="text-[10px] tracking-[0.35em] uppercase text-primary font-bold">Invite invalid</div>
                            <p className="text-sm text-white/80">{state.message}</p>
                            <Button variant="outline" onClick={() => nav("/login")} data-testid="invite-back-to-login" className="mt-2 bg-white text-black hover:bg-white/90">
                                Go to sign in
                            </Button>
                        </div>
                    )}
                </Card>

                <p className="text-[10px] text-white/40 text-center tracking-widest uppercase">© UTAL MEA LLC FZ · Demo Environment</p>
            </div>
        </div>
    );
}
